import {
  isReflectionUTF16Boundary,
  projectReflection,
  REFLECTION_ANCHOR_CONTRACT,
  REFLECTION_READER_CONTRACT,
  SAVED_NOTE_UNLOCATED,
  verifyReflectionAnchor,
} from './reflectionKeepProjection';

import type { MomentsArtifactNote } from '@/store/api/momentsApi';

// Wire metadata remains untrusted even when its DTO uses supported literals.
export const hasSupportedReflectionContracts = (
  reader: string,
  anchor: string,
) =>
  reader === REFLECTION_READER_CONTRACT &&
  anchor === REFLECTION_ANCHOR_CONTRACT;

export const reflectionNotePlacement = (
  note: MomentsArtifactNote,
  loaded:
    | { id: string; markdown: string; revision: number; profileSlug: string }
    | undefined,
  conservativeLegacy: boolean,
): MomentsArtifactNote => {
  const unlocated = () => ({
    ...note,
    blockId: SAVED_NOTE_UNLOCATED,
    endIndex: 0,
    startIndex: 0,
  });
  if (loaded && note.artifactId !== loaded.id) return unlocated();
  const identity = note.sourceIdentity;
  if (identity) {
    if (
      !loaded ||
      note.artifactId !== loaded.id ||
      loaded.profileSlug !== 'member-reflection' ||
      identity.revision !== loaded.revision ||
      !hasSupportedReflectionContracts(
        identity.readerContract,
        identity.anchorContract,
      )
    ) {
      return unlocated();
    }
    const blocks = projectReflection(loaded.markdown);
    if (
      !verifyReflectionAnchor(blocks, {
        ...identity,
        selectedText: note.selectedText,
      })
    ) {
      return unlocated();
    }
    return {
      ...note,
      blockId: identity.blockId,
      endIndex: identity.endIndex,
      startIndex: identity.startIndex,
    };
  }
  if (!conservativeLegacy) {
    return note;
  }
  if (!loaded || note.blockId.includes(':') || !note.selectedText) {
    return unlocated();
  }
  const blocks = projectReflection(loaded.markdown);
  const block = blocks.find((item) => item.id === note.blockId);
  if (
    !block ||
    !isReflectionUTF16Boundary(block.visibleText, note.startIndex) ||
    !isReflectionUTF16Boundary(block.visibleText, note.endIndex) ||
    note.startIndex >= note.endIndex ||
    block.visibleText.slice(note.startIndex, note.endIndex) !==
      note.selectedText
  ) {
    return unlocated();
  }
  let occurrences = 0;
  for (const item of blocks) {
    let from = 0;
    for (;;) {
      const found = item.visibleText.indexOf(note.selectedText, from);
      if (found < 0) {
        break;
      }
      occurrences += 1;
      if (occurrences > 1) {
        return unlocated();
      }
      from = found + 1;
    }
  }
  // Verified current text only; unknown original version remains null.
  return occurrences === 1 ? note : unlocated();
};
